// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import { recordProvenance } from '../utils/photoProvenance'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA',
}
const f = 'Georgia, serif'

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

const CATEGORIES = [
  { id: 'property',    label: 'Property',        icon: '🏠', examples: 'Purchase agreement, title, survey, HOA docs, tax records' },
  { id: 'warranty',   label: 'Warranties',       icon: '🛡️', examples: 'Appliance, roof, HVAC, windows' },
  { id: 'manual',     label: 'Manuals',          icon: '📖', examples: 'Owner manuals, installation guides' },
  { id: 'service',    label: 'Service Records',  icon: '🔧', examples: 'Invoices, permits, inspection reports' },
  { id: 'insurance',  label: 'Insurance',        icon: '📋', examples: 'Homeowners, flood, earthquake, home warranty' },
  { id: 'improvement',label: 'Improvements',     icon: '🏗️', examples: 'Contracts, permits, before/after' },
]

function getFileIcon(fileType) {
  if (!fileType) return '📄'
  if (fileType.includes('pdf')) return '📕'
  if (fileType.startsWith('image')) return '🖼️'
  if (fileType.includes('word') || fileType.includes('document')) return '📝'
  return '📄'
}

function formatFileSize(bytes) {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return dateStr }
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.4)',
    zIndex: 1000, fontFamily: f,
  },
  panel: {
    position: 'fixed', top: 0, right: 0, bottom: 0,
    width: '100%', maxWidth: '560px',
    background: C.card, boxShadow: '-8px 0 40px rgba(44,29,20,0.15)',
    borderLeft: `1px solid ${C.border}`, overflowY: 'auto', zIndex: 1001,
    display: 'flex', flexDirection: 'column',
  },
  header: {
    padding: '20px 24px', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', borderBottom: `1px solid ${C.border}`,
    position: 'sticky', top: 0, background: C.card, zIndex: 10, gap: '12px',
  },
  headerLeft: { flex: 1, minWidth: 0 },
  title: { fontSize: '20px', fontWeight: '700', color: C.brown },
  subtitle: { fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '2px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: '0', lineHeight: 1, flexShrink: 0 },
  uploadBtn: {
    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '8px', padding: '9px 16px',
    color: C.card, fontSize: '12px', fontWeight: '700',
    letterSpacing: '1px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: f, whiteSpace: 'nowrap', flexShrink: 0,
  },
  catBar: {
    padding: '12px 24px', borderBottom: `1px solid ${C.border}`,
    display: 'flex', gap: '8px', overflowX: 'auto',
    position: 'sticky', top: '65px', background: C.card, zIndex: 9,
    scrollbarWidth: 'none',
  },
  catPill: (active) => ({
    padding: '6px 14px', borderRadius: '20px', whiteSpace: 'nowrap',
    fontSize: '12px', fontFamily: 'Calibri, sans-serif', cursor: 'pointer',
    border: `1px solid ${active ? C.amber : C.border}`,
    background: active ? '#FBF3E8' : 'transparent',
    color: active ? C.amber : C.muted,
    fontWeight: active ? '700' : '400',
    transition: 'all 0.15s',
  }),
  body: { padding: '20px 24px', flex: 1 },
  docCard: {
    background: C.bg, borderRadius: '12px', border: `1px solid ${C.border}`,
    padding: '14px 16px', marginBottom: '10px',
    display: 'flex', alignItems: 'flex-start', gap: '12px',
  },
  docIcon: { fontSize: '28px', flexShrink: 0, marginTop: '2px' },
  docInfo: { flex: 1, minWidth: 0 },
  docName: { fontSize: '14px', fontWeight: '700', color: C.brown, marginBottom: '4px', wordBreak: 'break-word' },
  docMeta: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' },
  catBadge: () => ({
    fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
    background: '#FBF3E8', color: C.amber,
    border: `1px solid ${C.amber}33`,
    fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap',
  }),
  docSize: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  docDate: { fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  docNotes: { fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '4px', fontStyle: 'italic' },
  docActions: { display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 },
  viewBtn: {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: '7px',
    padding: '6px 12px', fontSize: '11px', color: C.brown, cursor: 'pointer',
    fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap',
  },
  deleteBtn: {
    background: 'transparent', border: `1px solid ${C.danger}33`, borderRadius: '7px',
    padding: '6px 12px', fontSize: '11px', color: C.danger, cursor: 'pointer',
    fontFamily: 'Calibri, sans-serif',
  },
  emptyState: {
    textAlign: 'center', padding: '40px 24px',
    background: C.card, borderRadius: '14px', border: `1px dashed ${C.border}`,
  },
  sectionLabel: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '10px', marginTop: '20px',
    fontFamily: 'Calibri, sans-serif',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1100, padding: '20px', fontFamily: f,
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%', maxWidth: '460px',
    boxShadow: '0 8px 40px rgba(44,29,20,0.2)', border: `1px solid ${C.border}`,
    maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: {
    padding: '22px 24px 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px',
  },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: C.brown },
  modalBody: { padding: '0 24px 24px' },
  label: {
    fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
    textTransform: 'uppercase', marginBottom: '5px', display: 'block',
    fontFamily: 'Calibri, sans-serif',
  },
  input: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 14px', fontSize: '14px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px',
  },
  select: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 14px', fontSize: '14px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', cursor: 'pointer',
    appearance: 'none', WebkitAppearance: 'none',
  },
  textarea: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 14px', fontSize: '14px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', marginBottom: '14px', resize: 'vertical', minHeight: '72px',
  },
  btnRow: { display: 'flex', gap: '10px', marginTop: '4px' },
  cancelBtn: {
    flex: 1, background: 'transparent', border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '12px', fontSize: '13px',
    color: C.muted, cursor: 'pointer', fontFamily: f,
  },
  saveBtn: (disabled) => ({
    flex: 1, background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '12px', fontSize: '13px',
    color: C.card, fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: f, opacity: disabled ? 0.6 : 1,
  }),
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '14px',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
}

export default function DocumentVault({ session, activePropertyId, appliances = [], systems = [], onClose }) {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')
  const [showSourcePicker, setShowSourcePicker] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [uploadForm, setUploadForm] = useState({ name: '', category: '', notes: '', linkedItemId: '' })
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [preCategory, setPreCategory] = useState('')

  // Three separate inputs: camera scan, all files, documents/PDFs only
  const scanInputRef = useRef(null)
  const chooseInputRef = useRef(null)
  const browseInputRef = useRef(null)

  useEffect(() => { fetchDocs() }, [activePropertyId])

  const fetchDocs = async () => {
    if (!activePropertyId) return
    const { data } = await supabase.from('documents').select('*')
      .eq('property_id', activePropertyId).order('created_at', { ascending: false })
    setDocs(data || [])
    setLoading(false)
  }

  const openSourcePicker = (cat = '') => {
    setPreCategory(cat)
    setUploadFile(null)
    setUploadForm({ name: '', category: cat, notes: '', linkedItemId: '' })
    setAlert(null)
    setShowSourcePicker(true)
  }

  const applyFile = (file, isScanned = false) => {
    if (file.size > 20 * 1024 * 1024) {
      setAlert({ type: 'error', msg: 'File must be under 20MB.' })
      return
    }
    setUploadFile(file)
    setAlert(null)
    setShowSourcePicker(false)

    // Auto-name based on source
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    const autoName = isScanned
      ? `Scanned Document ${today}`
      : file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
    setUploadForm(prev => ({ ...prev, name: prev.name || autoName, category: prev.category || preCategory }))
    setShowUpload(true)
  }

  const handleFileChange = (e, isScanned = false) => {
    const file = e.target.files?.[0]
    if (!file) return
    applyFile(file, isScanned)
    e.target.value = ''
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) applyFile(file, false)
  }

  const handleUpload = async () => {
    if (!uploadFile) { setAlert({ type: 'error', msg: 'Please select a file.' }); return }
    if (!uploadForm.name.trim()) { setAlert({ type: 'error', msg: 'Please enter a name.' }); return }
    if (!uploadForm.category) { setAlert({ type: 'error', msg: 'Please select a category.' }); return }
    setUploading(true)
    setAlert(null)
    try {
      const userId = session.user.id
      const safeName = uploadFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const filePath = `${userId}/documents/${Date.now()}_${safeName}`
      const { error: uploadError } = await supabase.storage
        .from('documents').upload(filePath, uploadFile, { upsert: false, contentType: uploadFile.type })
      if (uploadError) throw uploadError
      const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath)
      const { data: insertedDoc, error: dbError } = await supabase.from('documents').insert({
        user_id: userId,
        property_id: activePropertyId,
        name: uploadForm.name.trim(),
        category: uploadForm.category,
        file_url: urlData.publicUrl,
        file_path: filePath,
        file_size: uploadFile.size,
        file_type: uploadFile.type,
        notes: uploadForm.notes.trim() || null,
      }).select('id').single()
      if (dbError) throw dbError
        if (uploadForm.category === 'manual' && uploadForm.linkedItemId) {
        const [itemType, itemId] = uploadForm.linkedItemId.split(':')
        const table = itemType === 'appliance' ? 'appliances' : 'systems'
        await supabase.from(table).update({ manual_url: urlData.publicUrl }).eq('id', itemId)
      }
      if (uploadFile.type?.startsWith('image/') || uploadFile.type?.startsWith('video/')) {
        recordProvenance({
          file: uploadFile,
          storageBucket: 'documents',
          storagePath: filePath,
          resourceType: 'document',
          resourceId: insertedDoc?.id || null,
          homeId: null,
          userId,
        }).catch(() => {})
      }
      setShowUpload(false)
      setUploadFile(null)
      setUploadForm({ name: '', category: '', notes: '', linkedItemId: '' })
      fetchDocs()
    } catch {
      setAlert({ type: 'error', msg: 'Upload failed. Please try again.' })
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    setDeleting(true)
    try {
      await supabase.storage.from('documents').remove([deleteConfirm.file_path])
      await supabase.from('documents').delete().eq('id', deleteConfirm.id)
      setDeleteConfirm(null)
      fetchDocs()
    } catch {
      setAlert({ type: 'error', msg: 'Error deleting document.' })
    }
    setDeleting(false)
  }

  const setF = (field) => (e) => setUploadForm(p => ({ ...p, [field]: e.target.value }))

  const filteredDocs = activeCategory === 'all' ? docs : docs.filter(d => d.category === activeCategory)
  const catCount = (id) => docs.filter(d => d.category === id).length
  const groupedByCategory = activeCategory === 'all'
    ? CATEGORIES.map(cat => ({ cat, items: docs.filter(d => d.category === cat.id) })).filter(g => g.items.length > 0)
    : null

  return (
    <>
      {/* Hidden file inputs — available across all modals */}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={e => handleFileChange(e, true)}
      />
      <input
        ref={chooseInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx"
        style={{ display: 'none' }}
        onChange={e => handleFileChange(e, false)}
      />
      <input
        ref={browseInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt"
        style={{ display: 'none' }}
        onChange={e => handleFileChange(e, false)}
      />

      <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()} />
      <div style={S.panel}>

        {/* Header */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.title}>📁 Document Vault</div>
            <div style={S.subtitle}>{docs.length} document{docs.length !== 1 ? 's' : ''} stored</div>
          </div>
          <button
            style={S.uploadBtn}
            onClick={() => openSourcePicker(activeCategory !== 'all' ? activeCategory : '')}
          >+ Add Document</button>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Category filter */}
        <div style={S.catBar}>
          <div style={S.catPill(activeCategory === 'all')} onClick={() => setActiveCategory('all')}>
            All ({docs.length})
          </div>
          {CATEGORIES.map(cat => (
            <div key={cat.id} style={S.catPill(activeCategory === cat.id)} onClick={() => setActiveCategory(cat.id)}>
              {cat.icon} {cat.label} {catCount(cat.id) > 0 && `(${catCount(cat.id)})`}
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={S.body}>
          {loading ? (
            <div style={{ color: C.muted, fontStyle: 'italic', fontFamily: 'Calibri, sans-serif' }}>Loading...</div>
          ) : filteredDocs.length === 0 ? (
            <div style={S.emptyState}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>
                {activeCategory === 'all' ? '📁' : CATEGORIES.find(c => c.id === activeCategory)?.icon || '📄'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: C.brown, marginBottom: '6px' }}>
                {activeCategory === 'all' ? 'No documents yet' : `No ${CATEGORIES.find(c => c.id === activeCategory)?.label || ''} documents`}
              </div>
              {activeCategory !== 'all' && (
                <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '16px', lineHeight: 1.6 }}>
                  {CATEGORIES.find(c => c.id === activeCategory)?.examples}
                </div>
              )}
              {activeCategory === 'all' && (
                <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginBottom: '20px', lineHeight: 1.6 }}>
                  Store your important home documents — warranties, manuals, inspection reports, insurance policies, and more.
                </div>
              )}
              <button
                style={S.uploadBtn}
                onClick={() => openSourcePicker(activeCategory !== 'all' ? activeCategory : '')}
              >+ Add Document</button>
            </div>
          ) : activeCategory !== 'all' ? (
            filteredDocs.map(doc => <DocCard key={doc.id} doc={doc} onDelete={setDeleteConfirm} />)
          ) : (
            groupedByCategory.map(({ cat, items }) => (
              <div key={cat.id}>
                <div style={S.sectionLabel}>{cat.icon} {cat.label}</div>
                {items.map(doc => <DocCard key={doc.id} doc={doc} onDelete={setDeleteConfirm} />)}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── SOURCE PICKER MODAL ── */}
      {showSourcePicker && (
        <div style={S.modalOverlay} onClick={e => e.target === e.currentTarget && setShowSourcePicker(false)}>
          <div style={{ ...S.modal, maxWidth: '400px' }}>
            <div style={S.modalHeader}>
              <div style={S.modalTitle}>Add Document</div>
              <button style={S.closeBtn} onClick={() => setShowSourcePicker(false)}>×</button>
            </div>
            <div style={S.modalBody}>

              {/* Scan Document — primary on mobile, de-emphasized on desktop */}
              {isMobile ? (
                <button
                  onClick={() => scanInputRef.current?.click()}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                    border: 'none', borderRadius: '14px', padding: '18px 20px',
                    display: 'flex', alignItems: 'center', gap: '16px',
                    marginBottom: '10px', boxShadow: '0 4px 16px rgba(184,114,31,0.25)',
                  }}
                >
                  <div style={{ fontSize: '32px', lineHeight: 1 }}>📷</div>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: C.card, fontFamily: f, marginBottom: '3px' }}>Scan Document</div>
                    <div style={{ fontSize: '12px', color: 'rgba(253,250,246,0.85)', fontFamily: 'Calibri, sans-serif', lineHeight: 1.4 }}>
                      Point your rear camera at a warranty card, permit, invoice, or inspection report
                    </div>
                  </div>
                </button>
              ) : (
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ fontSize: '20px' }}>📷</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: C.muted, fontFamily: f }}>Scan Document</div>
                    <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif', marginTop: '1px' }}>Available on mobile — use rear camera to capture a physical document</div>
                  </div>
                </div>
              )}

              {/* Choose File */}
              <button
                onClick={() => chooseInputRef.current?.click()}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: isMobile ? C.bg : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
                  border: isMobile ? `1px solid ${C.border}` : 'none',
                  borderRadius: '14px', padding: '16px 20px',
                  display: 'flex', alignItems: 'center', gap: '16px',
                  marginBottom: '10px',
                  boxShadow: isMobile ? 'none' : '0 4px 16px rgba(184,114,31,0.25)',
                }}
              >
                <div style={{ fontSize: '28px', lineHeight: 1 }}>📁</div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: isMobile ? C.brown : C.card, fontFamily: f, marginBottom: '3px' }}>Choose File</div>
                  <div style={{ fontSize: '12px', color: isMobile ? C.muted : 'rgba(253,250,246,0.85)', fontFamily: 'Calibri, sans-serif', lineHeight: 1.4 }}>
                    Photos, images, PDFs — from your camera roll or files
                  </div>
                </div>
              </button>

              {/* Browse Files */}
              <button
                onClick={() => browseInputRef.current?.click()}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: C.bg, border: `1px solid ${C.border}`,
                  borderRadius: '14px', padding: '16px 20px',
                  display: 'flex', alignItems: 'center', gap: '16px',
                  marginBottom: '4px',
                }}
              >
                <div style={{ fontSize: '28px', lineHeight: 1 }}>📄</div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.brown, fontFamily: f, marginBottom: '3px' }}>Browse Files</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif', lineHeight: 1.4 }}>
                    PDFs and document files from your device storage
                  </div>
                </div>
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── UPLOAD DETAIL FORM ── */}
      {showUpload && (
        <div style={S.modalOverlay} onClick={e => e.target === e.currentTarget && setShowUpload(false)}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <div style={S.modalTitle}>
                {uploadFile ? getFileIcon(uploadFile.type) : '📄'} Document Details
              </div>
              <button style={S.closeBtn} onClick={() => setShowUpload(false)}>×</button>
            </div>
            <div style={S.modalBody}>
              {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

              {/* Selected file preview */}
              {uploadFile && (
                <div
                  style={{
                    background: C.bg, border: `1px solid ${C.amber}44`,
                    borderRadius: '10px', padding: '12px 14px', marginBottom: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <div style={{ fontSize: '22px', flexShrink: 0 }}>{getFileIcon(uploadFile.type)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: C.brown, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uploadFile.name}</div>
                      <div style={{ fontSize: '11px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>{formatFileSize(uploadFile.size)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowUpload(false); setShowSourcePicker(true) }}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', color: C.muted, cursor: 'pointer', fontFamily: 'Calibri, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >Change</button>
                </div>
              )}

              {/* Drop zone fallback for desktop users who want to drag-and-drop */}
              {!uploadFile && (
                <div
                  style={{ border: `2px dashed ${C.border}`, borderRadius: '10px', padding: '24px', textAlign: 'center', marginBottom: '14px', cursor: 'pointer', background: C.bg }}
                  onClick={() => chooseInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📎</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: C.brown, marginBottom: '4px' }}>Click or drag to upload</div>
                  <div style={{ fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>PDF, images, Word docs · Max 20MB</div>
                </div>
              )}

              <label style={S.label}>Document Name *</label>
              <input
                style={S.input}
                value={uploadForm.name}
                onChange={setF('name')}
                placeholder="e.g. Roof Warranty 2023"
                autoComplete="off"
              />

              <label style={S.label}>Category *</label>
              <select style={S.select} value={uploadForm.category} onChange={setF('category')}>
                <option value="">Select category...</option>
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                ))}
              </select>

              {uploadForm.category === 'manual' && (appliances.length > 0 || systems.length > 0) && (
                <>
                  <label style={S.label}>Link to Appliance / System (optional)</label>
                  <select style={S.select} value={uploadForm.linkedItemId} onChange={setF('linkedItemId')}>
                    <option value="">Don't link</option>
                    {appliances.map(a => (
                      <option key={a.id} value={`appliance:${a.id}`}>{a.nickname || a.name}{a.make ? ` — ${a.make}` : ''}</option>
                    ))}
                    {systems.map(s => (
                      <option key={s.id} value={`system:${s.id}`}>{s.name}</option>
                    ))}
                  </select>
                </>
              )}

              <label style={S.label}>Notes (optional)</label>
              <textarea
                style={S.textarea}
                value={uploadForm.notes}
                onChange={setF('notes')}
                placeholder="e.g. 10-year warranty, expires Dec 2033, contact: Owens Corning"
              />

              <div style={S.btnRow}>
                <button style={S.cancelBtn} onClick={() => setShowUpload(false)}>Cancel</button>
                <button style={S.saveBtn(uploading || !uploadFile)} onClick={handleUpload} disabled={uploading || !uploadFile}>
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM ── */}
      {deleteConfirm && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, maxWidth: '360px' }}>
            <div style={S.modalHeader}>
              <div style={S.modalTitle}>Delete Document?</div>
              <button style={S.closeBtn} onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div style={S.modalBody}>
              <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}33`, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px', color: C.danger, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
                Permanently delete <strong>"{deleteConfirm.name}"</strong>? This cannot be undone.
              </div>
              <div style={S.btnRow}>
                <button style={S.cancelBtn} onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button style={{ ...S.saveBtn(deleting), background: C.danger }} onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function DocCard({ doc, onDelete }) {
  const cat = CATEGORIES.find(c => c.id === doc.category)
  return (
    <div style={S.docCard}>
      <div style={S.docIcon}>{getFileIcon(doc.file_type)}</div>
      <div style={S.docInfo}>
        <div style={S.docName}>{doc.name}</div>
        <div style={S.docMeta}>
          {cat && <span style={S.catBadge(doc.category)}>{cat.icon} {cat.label}</span>}
          {doc.file_size && <span style={S.docSize}>{formatFileSize(doc.file_size)}</span>}
          <span style={S.docDate}>{formatDate(doc.created_at)}</span>
        </div>
        {doc.notes && <div style={S.docNotes}>{doc.notes}</div>}
      </div>
      <div style={S.docActions}>
        <button style={S.viewBtn} onClick={() => window.open(doc.file_url, '_blank')}>
          View ↗
        </button>
        <button style={S.deleteBtn} onClick={() => onDelete(doc)}>
          Delete
        </button>
      </div>
    </div>
  )
}
